import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { apiClient, ApiError } from '../../../lib/apiClient';
import Banner from './Banner';

interface BlockingCampaign {
  submission_id: string;
  campaign_name: string;
}

export interface ArchiveLibraryEntryModalProps {
  slug: string;
  advertiserId: string;
  filename: string;
  sizeBytes: number | null;
  // What the user sees as the file label — falls back to `filename`.
  displayName?: string;
  // When set, the modal toggles into the unarchive variant: same panel shape,
  // confirm button hits DELETE, no in-use guard.
  mode: 'archive' | 'unarchive';
  accentColor?: string;
  onClose: () => void;
  onSuccess: () => void;
}

// CTV-526: confirmation modal for archive / unarchive. Two stable states:
//   - default → confirm or dismiss.
//   - blocked → 409 response from the server; banner lists the campaigns
//     that prevent archive with deep links.
const ArchiveLibraryEntryModal: React.FC<ArchiveLibraryEntryModalProps> = ({
  slug,
  advertiserId,
  filename,
  sizeBytes,
  displayName,
  mode,
  accentColor,
  onClose,
  onSuccess,
}) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [blockingCampaigns, setBlockingCampaigns] = React.useState<BlockingCampaign[]>([]);
  const isArchive = mode === 'archive';
  const isBlocked = blockingCampaigns.length > 0;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    setBlockingCampaigns([]);
    const body = { advertiserId, filename, sizeBytes: sizeBytes ?? 0 };
    try {
      if (isArchive) {
        await apiClient.post(`/creatives/library/${slug}/archive`, body);
      } else {
        await apiClient.delete(`/creatives/library/${slug}/archive`, body);
      }
      onSuccess();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as { blockingCampaigns?: BlockingCampaign[] } | null;
        const blockers = body?.blockingCampaigns ?? [];
        if (blockers.length > 0) {
          setBlockingCampaigns(blockers);
        } else {
          setError(e.message);
        }
      } else {
        setError(e instanceof Error ? e.message : 'Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  };

  const heading = isBlocked
    ? "Can't archive — currently in use"
    : isArchive
      ? 'Archive this commercial?'
      : 'Restore this commercial?';
  const intro = isBlocked
    ? 'This file is still attached to one or more active campaigns.'
    : isArchive
      ? "It will move to your Archived section. You can restore it anytime."
      : 'It will return to your active library.';
  const confirmLabel = isArchive ? 'Archive' : 'Unarchive';

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
        onClick={(e) => {
          e.stopPropagation();
          if (!busy) onClose();
        }}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[440px] bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
        style={{
          boxShadow:
            '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-[3px]"
          style={{
            background: `linear-gradient(90deg, transparent, ${
              isBlocked ? '#dc2626' : accentColor || '#475569'
            }, transparent)`,
          }}
        />
        <div className="p-5">
          <div className="flex items-start justify-between mb-2.5">
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 leading-tight">
                {heading}
              </h3>
              <p className="text-[12px] text-slate-500 mt-1">
                {intro}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Close"
              className="p-1 -mr-1 -mt-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 truncate">
            {displayName ?? filename}
          </div>

          {isBlocked && (
            <div className="mt-4">
              <Banner tone="warn" label="Currently in use">
                <ul className="space-y-1.5 mt-1">
                  {blockingCampaigns.map((c) => (
                    <li key={c.submission_id}>
                      <Link
                        to={`/${slug}/my-campaigns/${c.submission_id}`}
                        className="text-[12.5px] font-medium text-rose-700 hover:underline"
                      >
                        {c.campaign_name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="text-[11.5px] text-slate-600 mt-2">
                  Wait for these campaigns to end, cancel them, or swap the
                  commercial — then archive will be available.
                </p>
              </Banner>
            </div>
          )}

          {error && (
            <p className="text-[12px] text-red-600 mt-3 leading-relaxed">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              {isBlocked ? 'Close' : 'Cancel'}
            </button>
            {!isBlocked && (
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: `linear-gradient(180deg, ${
                    accentColor || '#475569'
                  }, ${accentColor ? darken(accentColor) : '#1e293b'})`,
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px rgba(15,23,42,0.5)',
                }}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {confirmLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

function darken(hex: string): string {
  // Naive darken — pulls the color toward 0x00 by ~25%. Used so the gradient
  // top→bottom has perceived depth without requiring callers to pass both
  // ends explicitly.
  const match = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!match) return hex;
  const n = parseInt(match[1], 16);
  const r = Math.max(0, Math.floor(((n >> 16) & 0xff) * 0.75));
  const g = Math.max(0, Math.floor(((n >> 8) & 0xff) * 0.75));
  const b = Math.max(0, Math.floor((n & 0xff) * 0.75));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export default ArchiveLibraryEntryModal;
